import { createSelector } from "reselect";
import { sharedLib } from "@shared/services/shared.service";
import {
  BacktestingFilterStatus,
  BacktestingRunType,
  BacktestingStatus,
  BotType,
  FilterType,
  GetBacktestingRunsRequest,
} from "@shared/types";
import {
  dcaBotBacktestingFilters,
  signalsBotBacktestingFilters,
} from "@shared/slices/filters.slice";
import {
  testNumberListFilter,
  testRangeFilter,
  testStringListFilter,
} from "@shared/features/filters/utils";
import { QueryStatus } from "@reduxjs/toolkit/query";

const selectBacktestingRuns = (args?: GetBacktestingRunsRequest) =>
  createSelector(
    sharedLib.sharedApi.bots.endpoints.getBacktestingRuns.select(args),
    ({ data }): BacktestingRunType[] => {
      return (
        data?.data?.map((backtesting) => ({
          ...backtesting,
          net_pnl: {
            USD:
              backtesting.realised_pnl?.USD + backtesting.unrealised_pnl?.USD,
            BTC:
              backtesting.realised_pnl?.BTC + backtesting.unrealised_pnl?.BTC,
          },
        })) || []
      );
    }
  );

export const selectBacktestingLoadingStates = (
  args?: GetBacktestingRunsRequest
) =>
  createSelector(
    sharedLib.sharedApi.bots.endpoints.getBacktestingRuns.select(args),
    ({ status }) => ({
      loading: status === QueryStatus.pending,
      loaded: status === QueryStatus.fulfilled,
    })
  );

export const selectFilteredBacktestingRunsData = (
  args?: GetBacktestingRunsRequest
) => {
  const filters =
    args?.bot_type === BotType.SIGNAL
      ? signalsBotBacktestingFilters
      : dcaBotBacktestingFilters;
  return createSelector(
    selectBacktestingRuns(args),
    filters.selectors.selectFiltersData,
    (backtestingRunsData, filtersData) => {
      return backtestingRunsData?.filter((backtestingRun) => {
        const status =
          BACKTESTING_STATUS_TO_FILTER_STATUS[backtestingRun.status];
        return (
          testRangeFilter(
            filtersData[FilterType.TIME_FRAME]?.value?.range,
            backtestingRun.created_at
          ) &&
          testStringListFilter(
            filtersData[FilterType.BACKTESTING_STATUS]?.value,
            status
          ) &&
          testNumberListFilter(
            filtersData[FilterType.MULTI_GROUPS]?.value,
            backtestingRun.sub_group_id
          ) &&
          testStringListFilter(
            filtersData[FilterType.CLIENTS]?.value,
            backtestingRun.client_name
          ) &&
          testStringListFilter(
            filtersData[FilterType.SYMBOLS]?.value,
            backtestingRun.symbol || ""
          )
        );
      });
    }
  );
};

const BACKTESTING_STATUS_TO_FILTER_STATUS = {
  [BacktestingStatus.IN_PROGRESS]: BacktestingFilterStatus.IN_PROGRESS,
  [BacktestingStatus.COMPLETED]: BacktestingFilterStatus.COMPLETED,
  [BacktestingStatus.FAILED]: BacktestingFilterStatus.FAILED,
};
